// useMonthlyCalculation.js — 1:1급여 정산: 정산할 달·불러오기·선택·넣기/빼기·확정·확정 취소
//   2026-10-06 MonthlyCalculationPanel.jsx(331줄)에서 옮김 → 같은 날 UX 3단계에서 정리:
//   - 넣기/빼기·조건 변경·확정 뒤 다시 불러올 때 화면을 비우지 않는다(keep) → 보던 직원이 첫 직원으로 튕기던 문제 해결
//   - 정산할 달(급여 규칙 기본월)을 기억해 "왜 이 달인지" 안내에 쓴다
//   - 경고창(alert)·확인창(confirm) 대신 화면 안내와 확정 요약 창(ChargeConfirmDialog)이 확인을 받는다
//   서버 주소·보내는 값은 종전과 같다. PC·휴대폰 화면이 같이 쓴다.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import {
  nextMonthFirstFromValue,
  parseMonthValue,
  previousMonthValue,
} from './monthlyCalculationUtils';

export function pendingDecisionCount(rows) {
  return (rows || []).reduce((sum, row) => sum + rowPendingCount(row), 0);
}

export function rowPendingCount(row) {
  return (row?.events || []).filter((event) => event.chargeDecisionStatus === 'pending').length;
}

export function withIntegratedMenu(url, integratedMenuId) {
  if (!integratedMenuId) return url;
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}integrated_menu_id=${encodeURIComponent(integratedMenuId)}`;
}

function monthValueOf(data) {
  return `${data.year}-${String(data.month).padStart(2, '0')}`;
}

export function useMonthlyCalculation({ companyCode, integratedMenuId = null, assignments = [], onAssignmentsChanged }) {
  const initialMonth = useMemo(() => previousMonthValue(), []);
  const [monthValue, setMonthValue] = useState(initialMonth);
  const [defaultMonthValue, setDefaultMonthValue] = useState(null);
  const [settlementDate, setSettlementDate] = useState(() => nextMonthFirstFromValue(initialMonth));
  const [defaultMonthReady, setDefaultMonthReady] = useState(false);
  const [data, setData] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const userPickedMonthRef = useRef(false);
  const parsedMonth = useMemo(() => parseMonthValue(monthValue), [monthValue]);
  const normalizedCompanyCode = useMemo(() => String(companyCode || 'PINE').trim().toUpperCase(), [companyCode]);

  useEffect(() => {
    let alive = true;
    userPickedMonthRef.current = false;
    setDefaultMonthReady(false);

    async function applyDefaultMonth() {
      const res = await apiClient.get(withIntegratedMenu(`/api/admin/marketer-consultant/default-month?company_code=${encodeURIComponent(normalizedCompanyCode)}`, integratedMenuId));
      if (!alive) return;
      if (res.ok && res.data?.data) {
        const nextValue = monthValueOf(res.data.data);
        setDefaultMonthValue(nextValue);
        if (!userPickedMonthRef.current) {
          setMonthValue(nextValue);
          setSettlementDate(nextMonthFirstFromValue(nextValue));
          setSelectedId(null);
        }
      }
      setDefaultMonthReady(true);
    }

    applyDefaultMonth();
    return () => { alive = false; };
  }, [integratedMenuId, normalizedCompanyCode]);

  // opts.keep = 같은 달을 다시 불러올 때 지금 화면을 비우지 않는다(보던 직원·스크롤 유지)
  const load = useCallback(async (opts = {}) => {
    if (!defaultMonthReady) return;
    const keep = opts && opts.keep === true;
    setLoading(true);
    setError('');
    if (!keep) {
      setData(null);
      setConfirmation(null);
    }
    const params = new URLSearchParams({
      company_code: normalizedCompanyCode,
      year: String(parsedMonth.year),
      month: String(parsedMonth.month),
    });
    if (integratedMenuId) params.set('integrated_menu_id', integratedMenuId);
    const query = params.toString();
    const [res, confirmRes] = await Promise.all([
      apiClient.get(`/api/admin/marketer-consultant/monthly-calculation?${query}`),
      apiClient.get(`/api/admin/marketer-consultant/monthly-confirmations?${query}`),
    ]);
    setLoading(false);
    if (!res.ok) {
      setError(res.data?.message || '월별 계산을 불러오지 못했습니다.');
      setData(null);
    } else {
      setData(res.data || null);
    }
    if (confirmRes.ok) {
      const nextConfirmation = confirmRes.data?.confirmation || null;
      setConfirmation(nextConfirmation);
      if (nextConfirmation?.status === 'confirmed' && nextConfirmation.settlementDate) {
        setSettlementDate(nextConfirmation.settlementDate);
      }
    } else {
      setConfirmation(null);
    }
  }, [defaultMonthReady, integratedMenuId, normalizedCompanyCode, parsedMonth.month, parsedMonth.year]);

  useEffect(() => { load(); }, [load]);

  const reload = useCallback(() => load({ keep: true }), [load]);

  const isConfirmed = confirmation?.status === 'confirmed';
  const displayData = isConfirmed && confirmation?.snapshot?.rows ? confirmation.snapshot : data;
  const rows = useMemo(() => displayData?.rows || [], [displayData]);
  const totals = displayData?.totals || {};
  const selectedRow = rows.find((row) => row.assignmentId === selectedId) || rows[0] || null;
  const selectedConfirmedItem = selectedRow && isConfirmed
    ? (confirmation?.items || []).find((item) => Number(item.assignmentId) === Number(selectedRow.assignmentId))
    : null;
  const selectedAssignment = selectedRow
    ? assignments.find((assignment) => Number(assignment.id) === Number(selectedRow.assignmentId)) || selectedConfirmedItem?.assignment
    : null;
  const pendingChargeCount = pendingDecisionCount(rows);

  useEffect(() => {
    if (!rows.length) {
      if (!loading) setSelectedId(null);
      return;
    }
    if (!rows.some((row) => row.assignmentId === selectedId)) setSelectedId(rows[0].assignmentId);
  }, [rows, selectedId, loading]);

  function handleMonthChange(next) {
    userPickedMonthRef.current = true;
    const value = typeof next === 'string'
      ? next
      : `${next.year}-${String(next.month).padStart(2, '0')}`;
    setMonthValue(value);
    setSettlementDate(nextMonthFirstFromValue(value));
    setSelectedId(null);
  }

  async function handleDefaultMonth() {
    const res = await apiClient.get(withIntegratedMenu(`/api/admin/marketer-consultant/default-month?company_code=${encodeURIComponent(normalizedCompanyCode)}`, integratedMenuId));
    if (!res.ok || !res.data?.data) return;
    userPickedMonthRef.current = false;
    const value = monthValueOf(res.data.data);
    setDefaultMonthValue(value);
    setMonthValue(value);
    setSettlementDate(nextMonthFirstFromValue(value));
    setSelectedId(null);
  }

  function handleSelectRow(assignmentId) {
    setSelectedId(assignmentId);
  }

  async function handleRulesChanged() {
    await onAssignmentsChanged?.();
    await reload();
  }

  async function handleDecisionChange({ assignmentId, contractId, eventType, decisionStatus }) {
    if (isConfirmed) {
      setError('확정된 달은 확정 취소한 뒤에 넣기·빼기를 바꿀 수 있습니다.');
      return;
    }
    setLoading(true);
    const res = await apiClient.put('/api/admin/marketer-consultant/charge-decisions', {
      assignment_id: assignmentId,
      contract_id: contractId,
      event_type: eventType,
      decision_status: decisionStatus,
      ...(integratedMenuId ? { integrated_menu_id: integratedMenuId } : {}),
    });
    setLoading(false);
    if (!res.ok) {
      setError(res.data?.message || '넣기·빼기 저장에 실패했습니다.');
      return;
    }
    setError('');
    await reload();
  }

  // 확정 요약 창을 열어도 되는지 — 확인 필요가 남았으면 안내만(서버도 같은 이유로 막는다)
  function canConfirm() {
    if (!rows.length) return false;
    const pendingCount = pendingDecisionCount(rows);
    if (pendingCount > 0) {
      setError(`확인 필요 ${pendingCount.toLocaleString('ko-KR')}건이 남아 있어 확정할 수 없습니다. 각 직원의 '확인 필요 계약'에서 넣기 또는 빼기를 정해 주세요.`);
      return false;
    }
    return true;
  }

  async function submitConfirm() {
    if (!canConfirm()) return false;
    setLoading(true);
    const res = await apiClient.post('/api/admin/marketer-consultant/monthly-confirmations/confirm', {
      company_code: normalizedCompanyCode,
      year: parsedMonth.year,
      month: parsedMonth.month,
      settlement_date: settlementDate,
      ...(integratedMenuId ? { integrated_menu_id: integratedMenuId } : {}),
    });
    setLoading(false);
    if (!res.ok) {
      setError(res.data?.message || '확정에 실패했습니다.');
      return false;
    }
    setConfirmation(res.data?.confirmation || null);
    setError('');
    await reload();
    return true;
  }

  async function handleReopen() {
    if (!confirmation?.id) return;
    const ok = window.confirm(`${parsedMonth.year}년 ${parsedMonth.month}월분 1:1급여 확정을 취소할까요?\n마케터 정산서에서 빠지고, 다시 고칠 수 있게 됩니다.`);
    if (!ok) return;
    setLoading(true);
    const res = await apiClient.post(withIntegratedMenu(`/api/admin/marketer-consultant/monthly-confirmations/${confirmation.id}/reopen`, integratedMenuId), {});
    setLoading(false);
    if (!res.ok) {
      setError(res.data?.message || '확정 취소에 실패했습니다.');
      return;
    }
    setConfirmation(res.data?.confirmation || null);
    setError('');
    await reload();
  }

  return {
    monthValue,
    defaultMonthValue,
    parsedMonth,
    liveData: data,
    settlementDate,
    setSettlementDate,
    defaultMonthReady,
    loading,
    error,
    confirmation,
    isConfirmed,
    rows,
    totals,
    pendingChargeCount,
    selectedRow,
    selectedAssignment,
    load,
    reload,
    handleMonthChange,
    handleDefaultMonth,
    handleSelectRow,
    handleRulesChanged,
    handleDecisionChange,
    canConfirm,
    submitConfirm,
    handleReopen,
  };
}
